import React, { useState, useEffect, useMemo } from 'react';
import { Box, VStack, Dialog, Button, Text } from '@chakra-ui/react';
import { DialogContent } from '@/components/ui/dialog';
import { BottomSheet } from '@/components/ui/custom/bottom-sheet';
import { toaster } from '@/components/ui/toaster';
import ProfileCard from '@/components/profile/ProfileCard';
import ProfileMenu from '@/components/profile/ProfileMenu';
import ChargeMenu from '@/components/profile/ChargeMenu';
import LogoutMenu from '@/components/profile/LogoutMenu';
import VersionInfo from '@/components/profile/VersionInfo';
import { ChargeInputContent } from './ChargeInputContent';
import { ChargeCompleteContent } from './ChargeCompleteContent';
import { ProfileEditContent } from './ProfileEditContent';
import { PasswordChangeContent } from './PasswordChangeContent';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCharge } from '@/hooks/wallet/useCharge';
import { useProfile, useUpdateProfile } from '@/hooks/profile/useProfile';
import { useChangePassword } from '@/hooks/profile/useChangePassword';

const Profile = () => {
  const { data: profileData, isLoading: isLoadingProfile } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const chargeMutation = useCharge();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isChargeDrawerOpen, setIsChargeDrawerOpen] = useState(false);
  const [chargeCode, setChargeCode] = useState('');
  const [chargeStep, setChargeStep] = useState<'input' | 'complete'>('input');
  const [chargeAmount, setChargeAmount] = useState(0);

  // プロフィール編集用のstate
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editUserId, setEditUserId] = useState('');

  // プロフィールデータが取得できたら、編集フォームの初期値を設定
  useEffect(() => {
    if (profileData) {
      setEditDisplayName(profileData.userName);
      setEditUserId(profileData.accountId);
    }
  }, [profileData]);

  // パスワード変更用のstate
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const handleMenuClick = (menuName: string) => {
    console.log(`${menuName} clicked`);
    if (menuName === 'プロフィール編集') {
      setIsEditProfileOpen(true);
    }
  };

  const handleEditProfileSubmit = async () => {
    // バリデーション
    if (!editDisplayName.trim()) {
      toaster.create({
        title: '表示名を入力してください',
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!editUserId.trim()) {
      toaster.create({
        title: 'ユーザーIDを入力してください',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({
        accountId: editUserId.trim(),
        displayName: editDisplayName.trim(),
      });

      // トーストで成功を通知
      toaster.create({
        title: 'プロフィールを更新しました',
        type: 'success',
        duration: 3000,
      });

      setIsEditProfileOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'プロフィールの更新に失敗しました';
      toaster.create({
        title: 'エラー',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleChangePasswordClick = () => {
    setIsChangePasswordOpen(true);
  };

  // パスワード変更のバリデーション
  const passwordValidation = useMemo(() => {
    const isSamePassword = !!(currentPassword && newPassword && currentPassword === newPassword);
    const isPasswordMismatch = !!(
      newPassword &&
      newPasswordConfirm &&
      newPassword !== newPasswordConfirm
    );
    const isFormValid = !!(
      currentPassword &&
      newPassword &&
      newPasswordConfirm &&
      !isSamePassword &&
      !isPasswordMismatch
    );

    return {
      isSamePassword,
      isPasswordMismatch,
      isFormValid,
    };
  }, [currentPassword, newPassword, newPasswordConfirm]);

  const handleChangePasswordSubmit = async () => {
    // バリデーション
    if (!currentPassword) {
      toaster.create({
        title: '現在のパスワードを入力してください',
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!newPassword) {
      toaster.create({
        title: '新しいパスワードを入力してください',
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      toaster.create({
        title: '新しいパスワードが一致しません',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });

      // トーストで成功を通知
      toaster.create({
        title: 'パスワードを変更しました',
        type: 'success',
        duration: 3000,
      });

      setIsChangePasswordOpen(false);
      // フィールドをクリア
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'パスワードの変更に失敗しました';
      toaster.create({
        title: 'エラー',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleChargeClick = () => {
    setIsChargeDrawerOpen(true);
    setChargeStep('input');
  };

  const handleChargeSubmit = async () => {
    if (!chargeCode.trim()) {
      toaster.create({
        title: 'チャージコードを入力してください',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const result = await chargeMutation.mutateAsync({
        chargeCode: chargeCode.trim(),
      });

      setChargeAmount(result.amount);
      setChargeStep('complete');

      toaster.create({
        title: 'チャージが完了しました',
        description: `฿${result.amount.toLocaleString()} をチャージしました`,
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'チャージに失敗しました';
      toaster.create({
        title: 'エラー',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleChargeComplete = () => {
    setChargeCode('');
    setChargeAmount(0);
    setChargeStep('input');
    setIsChargeDrawerOpen(false);
  };

  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    console.log('ログアウト処理を実行');
    try {
      await logout();
      toaster.create({
        title: 'ログアウトしました',
        type: 'success',
        duration: 2000,
      });
      setIsLogoutDialogOpen(false);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('ログアウトエラー:', error);
      toaster.create({
        title: 'ログアウトに失敗しました',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handleLogoutCancel = () => {
    setIsLogoutDialogOpen(false);
  };

  useEffect(() => {
    // ホーム画面からチャージボタンで遷移してきた場合、ボトムシートを開く
    if (location.state?.openChargeDrawer) {
      setIsChargeDrawerOpen(true);
      setChargeStep('input');
      // stateをクリアして、戻るボタンで戻った時に再度開かないようにする
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  return (
    <Box w="full" h="100%" userSelect="none">
      <VStack gap={3} w="full" maxW="450px" mx="auto" mb="80px">
        <ProfileCard
          data={
            profileData
              ? {
                  userName: profileData.userName,
                  accountId: profileData.accountId,
                  transactionCount: profileData.transactionCount,
                  registeredAt: profileData.registeredAt,
                }
              : undefined
          }
          isLoading={isLoadingProfile}
        />
        <ProfileMenu
          onEditProfile={() => handleMenuClick('プロフィール編集')}
          onChangePassword={handleChangePasswordClick}
        />
        <ChargeMenu onClick={handleChargeClick} />
        <LogoutMenu onClick={handleLogoutClick} />
        <VersionInfo />
      </VStack>

      {/* チャージコード入力ボトムシート */}
      <BottomSheet
        isOpen={isChargeDrawerOpen}
        onOpenChange={open => {
          setIsChargeDrawerOpen(open);
          if (!open) {
            setChargeStep('input');
            setChargeCode('');
          }
        }}
        title={chargeStep === 'input' ? '残高チャージ' : 'チャージ完了'}
        description={chargeStep === 'input' ? 'チャージコードを入力してください' : ''}
      >
        {chargeStep === 'input' ? (
          <ChargeInputContent
            chargeCode={chargeCode}
            onChargeCodeChange={setChargeCode}
            onSubmit={handleChargeSubmit}
            isLoading={chargeMutation.isPending}
          />
        ) : (
          <ChargeCompleteContent
            chargeAmount={chargeAmount}
            chargeCode={chargeCode}
            onClose={handleChargeComplete}
          />
        )}
      </BottomSheet>

      {/* プロフィール編集ボトムシート */}
      <BottomSheet
        isOpen={isEditProfileOpen}
        onOpenChange={open => {
          setIsEditProfileOpen(open);
          if (!open && profileData) {
            // 閉じる時に元の値にリセット
            setEditDisplayName(profileData.userName);
            setEditUserId(profileData.accountId);
          }
        }}
        title="プロフィール編集"
        description="表示名とユーザーIDを変更できます"
      >
        <ProfileEditContent
          displayName={editDisplayName}
          userId={editUserId}
          onDisplayNameChange={setEditDisplayName}
          onUserIdChange={setEditUserId}
          onSubmit={handleEditProfileSubmit}
          isLoading={updateProfileMutation.isPending}
        />
      </BottomSheet>

      {/* パスワード変更ボトムシート */}
      <BottomSheet
        isOpen={isChangePasswordOpen}
        onOpenChange={open => {
          setIsChangePasswordOpen(open);
          if (!open) {
            // 閉じる時にフィールドをクリア
            setCurrentPassword('');
            setNewPassword('');
            setNewPasswordConfirm('');
          }
        }}
        title="パスワード変更"
        description="現在のパスワードと新しいパスワードを入力してください"
      >
        <PasswordChangeContent
          currentPassword={currentPassword}
          newPassword={newPassword}
          newPasswordConfirm={newPasswordConfirm}
          onCurrentPasswordChange={setCurrentPassword}
          onNewPasswordChange={setNewPassword}
          onNewPasswordConfirmChange={setNewPasswordConfirm}
          onSubmit={handleChangePasswordSubmit}
          isSamePassword={passwordValidation.isSamePassword}
          isPasswordMismatch={passwordValidation.isPasswordMismatch}
          isFormValid={passwordValidation.isFormValid}
          isLoading={changePasswordMutation.isPending}
        />
      </BottomSheet>

      {/* ログアウト確認ダイアログ */}
      <Dialog.Root
        open={isLogoutDialogOpen}
        onOpenChange={e => setIsLogoutDialogOpen(e.open)}
        placement="center"
      >
        <DialogContent maxW="400px">
          <Dialog.Header>
            <Dialog.Title fontSize="lg" fontWeight="bold">
              ログアウトしますか？
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <Text fontSize="sm" color="gray.600">
              ログアウトすると、再度ログインが必要になります。
            </Text>
          </Dialog.Body>
          <Dialog.Footer gap={3}>
            <Button variant="outline" onClick={handleLogoutCancel}>
              キャンセル
            </Button>
            <Button colorPalette="red" onClick={handleLogoutConfirm}>
              ログアウト
            </Button>
          </Dialog.Footer>
        </DialogContent>
      </Dialog.Root>
    </Box>
  );
};

export default Profile;
